import type { ImportBatchStatus, ImportErrorRow, ImportVerdict } from 'fuel-benchmark-contracts';
import type { StatusTone } from '../../components/ui/StatusText';

/**
 * Colour discipline (plan §2.2): the verdict is the one thing a preview table has to make obvious.
 * A rejected row is red, a row that will import with a blemish amber, a clean row green.
 */
export const verdictTone = (verdict: ImportVerdict): StatusTone => {
  switch (verdict) {
    case 'ACCEPT':
      return 'success';
    case 'WARN':
      return 'warning';
    case 'REJECT':
      return 'danger';
  }
};

/** A committed batch is the expected outcome and stays quiet; one that imported nothing is worth noticing. */
export const batchStatusTone = (status: ImportBatchStatus): StatusTone => (status === 'FAILED' ? 'danger' : 'muted');

/** `.xlsx` and `.csv` only — the two flavours the template comes in. */
export const ACCEPTED_UPLOAD_EXTENSIONS = '.xlsx,.csv';

const BASE64_MARKER = ';base64,';

export interface UploadPayload {
  filename: string;
  contentBase64: string;
}

/**
 * Reads the picked file as base64 in the browser, because that is how the import contracts carry a file:
 * `{ filename, contentBase64 }` for both `preview` and `commit`, and the same shape back from
 * `downloadTemplate`. A data URL is the shortest honest route to base64 in a browser — the prefix up to and
 * including `;base64,` is metadata we already know.
 */
export const readFileAsBase64 = (file: File): Promise<UploadPayload> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(reader.error ?? new Error('The file could not be read'));
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const marker = result.indexOf(BASE64_MARKER);

      if (marker < 0) {
        reject(new Error('The file could not be read as base64'));

        return;
      }

      resolve({ filename: file.name, contentBase64: result.slice(marker + BASE64_MARKER.length) });
    };

    reader.readAsDataURL(file);
  });

const base64ToBytes = (contentBase64: string): Uint8Array<ArrayBuffer> => {
  const binary = window.atob(contentBase64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

/** Hands the browser a file to save. Used for the template download and the error-report export alike. */
export const saveBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const saveBase64File = (file: { filename: string; contentBase64: string; mimeType: string }): void => {
  saveBlob(new Blob([base64ToBytes(file.contentBase64)], { type: file.mimeType }), file.filename);
};

/** Doubles the quotes a reason may contain, so a comma or a quote cannot break the column layout. */
const csvCell = (value: string | number): string => `"${String(value).replace(/"/g, '""')}"`;

export const ERROR_REPORT_HEADERS = ['row_number', 'code', 'reason'] as const;

/** The rejected rows as a spreadsheet, built client-side from the batch the API already returned. */
export const errorReportCsv = (rows: readonly ImportErrorRow[]): string =>
  [ERROR_REPORT_HEADERS.join(','), ...rows.map((row) => [csvCell(row.rowNumber), csvCell(row.code ?? ''), csvCell(row.reason)].join(','))].join('\n');

export const errorReportFilename = (batchFilename: string): string => `${batchFilename.replace(/\.[^.]+$/, '')}-import-errors.csv`;
